import { parse, stringify } from 'query-string';
import omit from 'lodash/omit';
import {
  SPP_PREVIEW_PATH_NAME,
  SPP_REVIEW_PATH_NAME,
} from 'chameleon/src/routes';

export const PARSE_OPTIONS = { arrayFormat: 'bracket' };

export const PAGE_ACTIONS = {
  CREATE_QUIZ: 'open-create-quiz',
  CREATE_LABEL: 'open-create-label',
  SHOW_HIDE_LABEL: 'open-show-hide-label',
};

const useNavigation = ({ router }) => {
  if (!router) {
    throw Error('useNavigation requires router as param');
  }

  const { location, routes } = router || {};
  const { pathname } = location || {};
  const queryParams = parse(location.search, PARSE_OPTIONS);
  const assignmentsPagePath = '/assignments';
  const isAssignmentsPage = pathname.endsWith(assignmentsPagePath);
  const assignmentProgressPath = `${assignmentsPagePath}/progress/`;
  const isAssignmentProgressPage = pathname.includes(assignmentProgressPath);
  const homePagePath = '/home';
  const adminHomePagePath = `${homePagePath}/`;
  const isHomePage =
    pathname.endsWith(homePagePath) || pathname.endsWith(adminHomePagePath);
  const dashboardPath = '/dashboard';
  const isDashboardPage = pathname.endsWith(dashboardPath);
  const activityReportPage = '/activities';
  const isActivityReportPage = pathname.includes(activityReportPage);
  const resultsPath = '/results';
  const isResultsPage = pathname.includes(resultsPath);
  const qbPath = '/question_bank';
  const isQuestionBankPage = pathname.includes(qbPath);
  const qbAllQuestionsPath = `${qbPath}/questions`;
  const isAllQuestionsQBPage = pathname.includes(qbAllQuestionsPath);
  const qbBuildQuizPath = `${qbPath}/create`;
  const isBuildQuizQBPage = pathname.includes(qbBuildQuizPath);
  const isQBPageWithCreateQuiz =
    queryParams?.action === PAGE_ACTIONS.CREATE_QUIZ;
  const isAssignmentsPageWithCreateEditLabelModal =
    queryParams?.action === PAGE_ACTIONS.CREATE_LABEL;
  const isAssignmentsPageWithLabelVisibilityModal =
    queryParams?.action === PAGE_ACTIONS.SHOW_HIDE_LABEL;
  const usagePath = '/usage';
  const isUsageReportPage = pathname.includes(usagePath);
  const routeNames = (routes || []).map((r) => r.name);
  const isSharedAssessmentPage = routeNames.indexOf('sharedAssessment') > -1;
  const isSppTeacherPreviewPage =
    routeNames.indexOf(SPP_PREVIEW_PATH_NAME) > -1;
  const isSppTeacherReviewPage = routeNames.indexOf(SPP_REVIEW_PATH_NAME) > -1;
  const isMasteryReportPage = pathname.includes('/csperformance');
  const isAuthorNewQuestionPage = (type = '') =>
    pathname.includes('/author/question') &&
    (type === '' || router.params?.qtype === type);

  const hasLabelsParams = !!queryParams.labels;

  const cleanActionParams = () => {
    router.push(replaceParamsInLocation(omit(queryParams, 'action')));
  };

  const addParamsToCurrentLocation = (
    params,
    newPath = null,
    omitParams = [],
  ) => {
    const newParams = omit(
      {
        ...queryParams,
        ...params,
      },
      omitParams,
    );

    return `${newPath || pathname}?${stringify(newParams, PARSE_OPTIONS)}`;
  };

  const replaceParamsInLocation = (params, newPath = null) => {
    return `${newPath || pathname}?${stringify(
      {
        ...params,
      },
      PARSE_OPTIONS,
    )}`;
  };

  const pushParamsToHistory = (params, newPath = null, omitParams = []) => {
    router.push(
      addParamsToCurrentLocation(params, newPath || pathname, omitParams),
    );
  };

  const goTo = (path) => router.push(path);

  const navigateWithParams = (params) => {
    goTo(addParamsToCurrentLocation(params));
  };

  return {
    addParamsToCurrentLocation,
    cleanActionParams,
    replaceParamsInLocation,
    pushParamsToHistory,
    navigateWithParams,
    queryParams, // use them on filter and search
    isAssignmentsPage,
    isAssignmentProgressPage,
    params: router.params,
    location,
    search: location.search,
    goTo,
    assignmentsPagePath,
    homePagePath,
    activityReportPage,
    resultsPath,
    isHomePage,
    isDashboardPage,
    isActivityReportPage,
    isResultsPage,
    isQuestionBankPage,
    isAllQuestionsQBPage,
    qbAllQuestionsPath,
    isBuildQuizQBPage,
    isQBPageWithCreateQuiz,
    isAssignmentsPageWithCreateEditLabelModal,
    isAssignmentsPageWithLabelVisibilityModal,
    isUsageReportPage,
    isSharedAssessmentPage,
    assignmentProgressPath,
    isMasteryReportPage,
    isAuthorNewQuestionPage,
    isSppTeacherPreviewPage,
    isSppTeacherReviewPage,
    hasLabelsParams,
  };
};

export default useNavigation;
